import { CallAPI } from "apiManager/ApiCall";
import { publicURL } from "apiManager/APIConfig";

export const DEMO_BOOKING_PUBLIC_KEY = "demo_booking_slots";
export const DEFAULT_DEMO_TIMEZONE = "Europe/Dublin";
const DEMO_BOOKING_WEEKS_AHEAD = 3;

const WEEKDAY_TO_INDEX = {
  sunday: 0,
  sun: 0,
  monday: 1,
  mon: 1,
  tuesday: 2,
  tue: 2,
  tues: 2,
  wednesday: 3,
  wed: 3,
  thursday: 4,
  thu: 4,
  thur: 4,
  thurs: 4,
  friday: 5,
  fri: 5,
  saturday: 6,
  sat: 6,
};

const INTL_WEEKDAY_TO_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

function getPublicDataString(items, key) {
  if (!Array.isArray(items)) return undefined;
  const row = items.find((i) => i?.key === key);
  if (!row?.data) return undefined;
  const s = String(row.data).trim();
  return s.length ? s : undefined;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toYmd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseYmd(ymd) {
  const [y, m, day] = ymd.split("-").map(Number);
  return new Date(y, m - 1, day);
}

function normalizeTime(value) {
  if (value == null) return null;
  const s = String(value).trim().toLowerCase();
  if (s === "14:00" || s === "2pm" || s === "2:00 pm" || s === "2:00pm") return "14:00";
  if (s === "17:00" || s === "5pm" || s === "5:00 pm" || s === "5:00pm") return "17:00";
  if (s === "14:00" || s === "17:00") return s;
  return null;
}

function normalizeWeekdayIndex(value) {
  const key = String(value || "")
    .trim()
    .toLowerCase();
  return WEEKDAY_TO_INDEX[key] ?? null;
}

export function formatTimezoneDisplay(timeZone = DEFAULT_DEMO_TIMEZONE) {
  const parts = new Intl.DateTimeFormat("en-IE", {
    timeZone,
    timeZoneName: "longOffset",
  }).formatToParts(new Date());
  const offset =
    parts.find((p) => p.type === "timeZoneName")?.value?.replace(/Greenwich Mean Time/i, "GMT") ||
    "GMT";
  if (timeZone === "Europe/Dublin") {
    return `Irish local time (${offset})`;
  }
  return `${timeZone} (${offset})`;
}

function getYmdInTimezone(date, timeZone) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function addDaysYmd(ymd, days) {
  const d = parseYmd(ymd);
  d.setDate(d.getDate() + days);
  return toYmd(d);
}

function getWeekdayIndexInTimezone(ymd, timeZone) {
  const [y, m, day] = ymd.split("-").map(Number);
  const utcNoon = new Date(Date.UTC(y, m - 1, day, 12, 0, 0));
  const short = new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short" }).format(utcNoon);
  return INTL_WEEKDAY_TO_INDEX[short] ?? -1;
}

export function expandScheduleToSlots(schedule, weeksAhead, timeZone) {
  const rules = schedule
    .map((row) => ({
      weekday: normalizeWeekdayIndex(row.weekday),
      times: (Array.isArray(row.times) ? row.times : [])
        .map(normalizeTime)
        .filter(Boolean),
    }))
    .filter((r) => r.weekday != null && r.times.length > 0);

  if (!rules.length) return [];

  const todayYmd = getYmdInTimezone(new Date(), timeZone);
  const lastYmd = addDaysYmd(todayYmd, weeksAhead * 7);
  const out = [];
  const seen = new Set();

  let cursor = todayYmd;
  while (cursor <= lastYmd) {
    const dow = getWeekdayIndexInTimezone(cursor, timeZone);
    for (const rule of rules) {
      if (rule.weekday !== dow) continue;
      for (const time of rule.times) {
        const key = `${cursor}_${time}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ date: cursor, time });
      }
    }
    cursor = addDaysYmd(cursor, 1);
  }

  out.sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
  return out;
}

export function parseDemoBookingFromPublicConfig(parsed) {
  if (parsed == null || typeof parsed !== "object") return null;

  const timeZone = String(parsed.timezone || DEFAULT_DEMO_TIMEZONE).trim();
  const weeksAhead = Math.max(1, Math.min(12, Number(parsed.weeksAhead) || DEMO_BOOKING_WEEKS_AHEAD));

  if (Array.isArray(parsed.schedule) && parsed.schedule.length > 0) {
    return {
      slots: expandScheduleToSlots(parsed.schedule, weeksAhead, timeZone),
      timezone: timeZone,
    };
  }

  return null;
}

export function parseDemoBookingSlotsFromPublicItems(items) {
  const raw = getPublicDataString(items, DEMO_BOOKING_PUBLIC_KEY);
  if (!raw) return null;
  try {
    return parseDemoBookingFromPublicConfig(JSON.parse(raw));
  } catch {
    return null;
  }
}

export async function fetchDemoBookingAvailability() {
  try {
    const res = await CallAPI({ url: publicURL, method: "GET" });
    const items = Array.isArray(res?.data) ? res.data : [];
    const parsed = parseDemoBookingSlotsFromPublicItems(items);
    const timezoneLabel = formatTimezoneDisplay(parsed?.timezone || DEFAULT_DEMO_TIMEZONE);

    if (parsed === null || parsed.slots.length === 0) {
      return { slots: [], fullyBooked: true, timezoneLabel };
    }
    return { slots: parsed.slots, fullyBooked: false, timezoneLabel };
  } catch {
    return {
      slots: [],
      fullyBooked: true,
      timezoneLabel: formatTimezoneDisplay(DEFAULT_DEMO_TIMEZONE),
    };
  }
}

export function groupSlotsByDate(slots) {
  const map = new Map();
  for (const s of slots) {
    const prev = map.get(s.date) || [];
    if (!prev.includes(s.time)) prev.push(s.time);
    map.set(s.date, prev);
  }
  return [...map.entries()]
    .map(([date, times]) => ({ date, times: times.sort() }))
    .sort((a, b) => a.date.localeCompare(b.date));
}
